<template>
  <main v-if="status">
    <p>{{ status }}</p>
    <loading />
  </main>
  <main v-else>
    <h2>Enroll in Project</h2>

    <div class="new-project">
      <p>
        <RouterLink to="/new" class="btn">Create your own project</RouterLink>
      </p>
    </div>

    <p>... or enroll in one of these templates</p>

    <h3 class="main">Templates:</h3>

    <div class="filter-section">
      <div class="filter-row">
        <input
          type="text"
          v-model="searchText"
          placeholder="Search in name or description..."
          class="search-input"
        />
      </div>

      <div class="filter-row" v-if="allSkills.length > 0">
        <div class="skills-filter-header">
          <label class="filter-label">Filter by skills:</label>
          <select v-model="skillFilterMode" class="skill-mode-select">
            <option value="any">ANY (at least one)</option>
            <option value="all">ALL (must have all)</option>
          </select>
        </div>
        <div class="skill-filters">
          <label
            v-for="skill in allSkills"
            :key="skill.id"
            :style="{
              backgroundColor: skill.color,
              color: getTextColor(skill.color)
            }"
            class="skill-checkbox"
            :class="{ active: selectedSkillIds.includes(skill.id) }"
          >
            <input
              type="checkbox"
              :value="skill.id"
              v-model="selectedSkillIds"
            />
            {{ skill.name }} ({{ skill.weight }})
          </label>
        </div>
      </div>

      <div class="filter-results" v-if="searchText || selectedSkillIds.length > 0">
        Found {{ filteredTemplates.length }} template(s)
      </div>
    </div>

    <div v-for="template in filteredTemplates" :key="template.id" class="template-container">
      <h3 class="main">{{ template.name }}</h3>
      <div class="template">
        <div class="template-description">
          <p class="markdown-content" v-html="markdown(template.description)"></p>
        </div>
        <div class="template-attributes">
          <p class="video" v-if="template.video_required">🎬</p>
          <dl>
            <dt>Skills:</dt>
            <dd v-for="skill in template.skills" :key="skill.id" :style="{ backgroundColor: skill.color }">
              <RouterLink :to="{ name: 'skill-detail', params: { id: skill.id } }"
                :style="{ color: getTextColor(skill.color) }">
                {{ skill.name }}({{ skill.weight }})
              </RouterLink>
            </dd>
          </dl>
          <p>
            <button @click="enroll(template.id, template.name)">Create new project from this template</button>
          </p>
        </div>
      </div>
    </div>
  </main>
</template>

<style scoped lang="scss">
@use 'sass:color';

.new-project {
  text-align: center;
  margin: 2rem auto;

  a {
    display: inline-block;
    font-size: 1.5rem;
    width: 77%;
    background-color: $btnColor;
    color: white;
    padding: 1rem;
    border-radius: 122px;
    border: none;
    cursor: pointer;
  }
}

h3.main {
  margin-left: 1rem;
  text-decoration: underline;
  font-size: 2rem;
  color: $primari;
}

.filter-section {
  margin: 0 0 2rem 0;
  padding: 1rem;
  background-color: rgba($primari, 0.05);
  border-radius: 8px;
  border: 1px solid rgba($primari, 0.2);
}

.filter-row {
  margin-bottom: 1rem;

  &:last-of-type {
    margin-bottom: 0;
  }
}

.search-input {
  width: 100%;
  padding: 0.7rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: $primari;
    box-shadow: 0 0 0 2px rgba($primari, 0.1);
  }
}

.filter-label {
  display: block;
  font-weight: bold;
  margin-bottom: 0.5rem;
  color: #333;
}

.skills-filter-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.skill-mode-select {
  padding: 0.4rem 0.6rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: white;
  font-size: 0.9rem;
  font-family: inherit;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: $primari;
  }

  &:hover {
    border-color: $primari;
  }
}

.skill-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.skill-checkbox {
  display: inline-flex;
  align-items: center;
  padding: 0.1rem 0.1rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: normal;
  opacity: 0.7;
  border: 2px solid transparent;

  &.active {
    opacity: 1;
    border: 2px solid rgba(0, 0, 0, 0.3);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  input[type="checkbox"] {
    margin-right: 0.4rem;
    cursor: pointer;
  }
}

.filter-results {
  margin-top: 1rem;
  padding: 0.5rem;
  text-align: center;
  font-size: 0.9rem;
  color: #666;
  font-style: italic;
}

.template-container {
  border: 1px solid #ddd;
  margin: 1rem auto;

  &:hover {
    box-shadow: 0px 0px 7px 7px $primari;
  }

  .template {
    display: flex;
  }

  .template-description {
    flex: 10;
    border: 2px solid #ccc;
    max-height: 33rem;
    margin: 1rem;
    padding: 1rem;
    margin-bottom: 1rem;
    overflow: auto;
  }

  .template-attributes {
    flex: 2;
    //max-height: 30rem;
    margin-bottom: 1rem;
    overflow: auto;

    p {
      margin: 0 0 1rem 0;

      :first-child {
        font-weight: bold;
      }
    }

    dt {
      font-weight: bold;
    }

    dd {
      display: inline-block;
      margin: 0.3em;
      border-radius: 1ex;

      a {
        display: inline-block;
        padding: 0.5em;
      }
    }
    button {
      margin: 2rem 12px;
      font-size: 1.1rem;
      color: white;
      background-color: $btnColor;
      padding: 0.5rem;
      border-radius: 0.5rem;
      cursor: pointer;
    }
  }
}
</style>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { RouterLink } from 'vue-router'
import { api } from '../utils/axios'
import { markdown, getTextColor } from '../utils/helpers'
import loading from '@/components/loading.vue'
import confirmDialog from '@/utils/confirmDialog'
import { useMessagesStore } from '@/stores/messages'
import router from '../router'


const messages = useMessagesStore()

const templates = ref([])
const status = ref('loading')

// Filtrační proměnné
const searchText = ref('')
const selectedSkillIds = ref([])
const skillFilterMode = ref('any')  // 'all' nebo 'any'

// Computed: Všechny unikátní skills ze všech templates
const allSkills = computed(() => {
  const skillsMap = new Map()
  templates.value.forEach(template => {
    template.skills.forEach(skill => {
      if (!skillsMap.has(skill.id)) {
        skillsMap.set(skill.id, skill)
      }
    })
  })
  return Array.from(skillsMap.values()).sort((a, b) =>
    a.name.localeCompare(b.name, 'cs')
  )
})

// Computed: Filtrované templates
const filteredTemplates = computed(() => {
  return templates.value.filter(template => {
    // Textový filtr - hledá v názvu a popisu (case-insensitive)
    const textMatch = searchText.value === '' ||
      template.name.toLowerCase().includes(searchText.value.toLowerCase()) ||
      template.description.toLowerCase().includes(searchText.value.toLowerCase())

    // Skill filtr
    let skillMatch = true
    if (selectedSkillIds.value.length > 0) {
      if (skillFilterMode.value === 'all') {
        // AND logika - template musí obsahovat VŠECHNY vybrané skills
        skillMatch = selectedSkillIds.value.every(selectedId =>
          template.skills.some(skill => skill.id === selectedId)
        )
      } else {
        // OR logika - template musí obsahovat ALESPOŇ JEDEN vybraný skill
        skillMatch = template.skills.some(skill =>
          selectedSkillIds.value.includes(skill.id)
        )
      }
    }

    return textMatch && skillMatch
  })
})

onMounted(async () => {
  try {
    const response = await api.get('/templates')
    templates.value = response.data
    status.value = null
  } catch (error) {
    status.value = error.response?.data?.msg || 'Error fetching projects data'
  }
})


async function enroll(templateId, templateName) {

    const confirmed = await confirmDialog.confirm({
    title: `${templateName}`,
    message: 'Really create a new project from this template?',
    confirmText: 'Yes',
    cancelText: 'No',
  })
  if (!confirmed) return

  try {
    const response = await api.post(`/project/fromtemplate/${templateId}`)
    messages.success('👍' + response.data.msg)
    router.push(`/project/${response.data.project_id}`)
  } catch (error) {
    messages.error('❌ Error: ' + (error.response?.data?.msg || error.message))
  }
}


</script>
